/**
 * Advertising click ids. Eligible for forwarding only while ads consent is on.
 * Callers must check `allowsAds()` before copying these.
 */
import { clickIdsForwardable } from 'lib/consent/gate';

export const AD_CLICK_ID_KEYS: readonly string[] = [
  'gclid',
  'fbclid',
  'ttclid',
  'gbraid',
  'wbraid',
  'gclsrc',
  'gad_source',
  'gad_campaignid',
  'fbadid',
  'tw_source',
  'tw_adid',
  'tw_campaign',
  'rdt_cid',
  'msclkid',
  'dclid',
  'twclid',
  'li_fat_id',
  'ScCid',
  'epik',
  'srsltid',
  // Google cross-domain linker; carries the _gcl/_ga ids.
  '_gl',
  // Affiliate click ids + sub ids (Impact, ShareASale), ads-consent gated.
  'irclickid',
  'subId1',
  'subId2',
  'subId3',
  'rvo_aff',
  'sscid',
  'click_id',
];

// Case-insensitive so `subid1` / `GCLID` variants copied from a URL are held too.
const AD_CLICK_ID_SET = new Set<string>(AD_CLICK_ID_KEYS.map((k) => k.toLowerCase()));

export function isAdClickId(key: string): boolean {
  return AD_CLICK_ID_SET.has(key.toLowerCase());
}

/**
 * Advertising click ids stay off internal links until ads consent is on and
 * the first client render has finished, so the href matches the server HTML.
 */
export function withoutHeldAdClickIds(source: URLSearchParams | string): URLSearchParams {
  const params = new URLSearchParams(
    typeof source === 'string' ? source : source.toString(),
  );
  if (clickIdsForwardable()) return params;
  for (const key of [...params.keys()]) {
    if (isAdClickId(key)) params.delete(key);
  }
  return params;
}

export function omitHeldAdClickIds<T extends Record<string, unknown>>(query: T): T {
  if (clickIdsForwardable()) return query;
  const next = { ...query };
  for (const key of Object.keys(next)) {
    if (isAdClickId(key)) delete next[key];
  }
  return next;
}

/** Path plus the current query, with advertising click ids removed until ads consent. */
export function pathWithHeldClickIdsRemoved(
  path: string,
  search: string | null | undefined,
): string {
  const qs = withoutHeldAdClickIds(search ?? '').toString();
  return `${path}${qs ? `?${qs}` : ''}`;
}

/** Query keys copied onto internal links. Ad click ids stay off until ads consent. */
export const FORWARDED_PARAM_KEYS: readonly string[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'affiliate',
  'affiliateCode',
  'referral',
  'discount',
  'sscid',
  'irclickid',
  'subId1',
  'subId2',
  'subId3',
  'rvo_aff',
  'click_id',
  'flow',
  'gclid',
  'fbclid',
  'ttclid',
  'gbraid',
  'wbraid',
  'gclsrc',
  'gad_source',
  'gad_campaignid',
  // Ad ids from the Meta / Google ad URL templates (tw_* feed Triple Whale).
  'fbadid',
  'tw_source',
  'tw_adid',
  'tw_campaign',
  // Reddit click id. Forwarded to the Shopify checkout the same way gclid is,
  // so it lands on the order as attributes[rdt_cid] for Reddit CAPI matching.
  'rdt_cid',
] as const;
